import { SweetAlertIcon } from "~/types/sweetalert";
import { useExport3D } from "~/composables/export3D";

// AR retry configuration (matching Shopify PDP logic)
const MAX_AR_RETRIES = 10; // 5 seconds total (10 × 500ms)
const MAX_ELEMENT_RETRIES = 5; // 2.5 seconds for element to appear
const AR_RETRY_INTERVAL = 500; // 500ms between retries

export function useNewStudioARAutoLaunch() {
  const { $toast } = useNuxtApp();
  const { exportToGLB, prepareForAR } = useExport3D();
  const gridStore = useGridStore();
  const projectStore = useProjectStore();

  const arModalOpen = ref(false);
  const arModelUrl = ref<string | null>(null);
  const arPageUrl = ref<string | null>(null);
  const arError = ref<string | null>(null);
  const isExportingAR = ref(false);
  const arAutoLaunch = ref(false);
  const arRetryTimer = ref<ReturnType<typeof setTimeout> | null>(null);

  const detectARSupport = () => {
    if (!import.meta.client)
      return {
        isIOS: false,
        isAndroid: false,
        isNonSafariiOS: false,
        supportsAR: false,
        isMobileDevice: false,
      };

    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);
    const isMobileDevice = isIOS || isAndroid;

    const a = document.createElement("a");
    const supportsQuickLook = a.relList?.supports?.("ar") || false;
    const isNonSafariiOS = isIOS && !supportsQuickLook;

    const supportsAR =
      isMobileDevice && ((isIOS && supportsQuickLook) || isAndroid);

    return { isIOS, isAndroid, isNonSafariiOS, supportsAR, isMobileDevice };
  };

  const tryLaunchAR = (retryCount = 0) => {
    if (!import.meta.client) return;

    const modelViewer = document.querySelector("model-viewer") as
      | (HTMLElement & { canActivateAR?: boolean; activateAR?: () => void })
      | null;

    if (modelViewer?.canActivateAR && modelViewer.activateAR) {
      console.log("[AR Auto-Launch] AR ready, activating...");
      modelViewer.activateAR();
      setTimeout(() => {
        arModalOpen.value = false;
      }, 300);
    } else if (modelViewer && retryCount < MAX_AR_RETRIES) {
      console.log(
        `[AR Auto-Launch] Model not ready, retry ${retryCount + 1}/${MAX_AR_RETRIES}`,
      );
      arRetryTimer.value = setTimeout(
        () => tryLaunchAR(retryCount + 1),
        AR_RETRY_INTERVAL,
      );
    } else if (!modelViewer && retryCount < MAX_ELEMENT_RETRIES) {
      console.log(
        `[AR Auto-Launch] Element not found, retry ${retryCount + 1}/${MAX_ELEMENT_RETRIES}`,
      );
      arRetryTimer.value = setTimeout(
        () => tryLaunchAR(retryCount + 1),
        AR_RETRY_INTERVAL,
      );
    } else {
      console.log(
        "[AR Auto-Launch] Exhausted retries, showing 3D preview instead",
      );
      arAutoLaunch.value = false;
    }
  };

  const handleARAutoLaunch = async () => {
    if (!import.meta.client) return;

    const { isNonSafariiOS, supportsAR, isMobileDevice } = detectARSupport();

    console.log("[AR Auto-Launch] Starting...", {
      isNonSafariiOS,
      supportsAR,
      isMobileDevice,
    });

    if (isNonSafariiOS) {
      console.log(
        "[AR Auto-Launch] iOS non-Safari detected, showing Open in Safari modal",
      );
      arAutoLaunch.value = false;
      arModalOpen.value = true;
      arError.value = null;
      let host = window.location.host;
      if (host.includes("localhost")) {
        host = "10.69.42.9:3000";
      }
      arPageUrl.value = window.location.href;
      return;
    }

    if (!isMobileDevice) {
      console.log("[AR Auto-Launch] Desktop detected, showing 3D preview");
    }

    if (gridStore.shapeList.length === 0) {
      console.log(
        "[AR Auto-Launch] No shapes in design, cannot generate model",
      );
      $toast.fire({
        icon: SweetAlertIcon.WARNING,
        title: "No tiles in this design",
      });
      return;
    }

    isExportingAR.value = true;
    arModalOpen.value = true;
    arError.value = null;
    arAutoLaunch.value = supportsAR;

    try {
      console.log("[AR Auto-Launch] Exporting to GLB...");
      const glbData = await exportToGLB();
      console.log(
        `[AR Auto-Launch] GLB exported, size: ${(glbData.byteLength / 1024).toFixed(1)}KB`,
      );

      console.log("[AR Auto-Launch] Uploading model...");
      const { glbUrl } = await prepareForAR(glbData);
      console.log("[AR Auto-Launch] Model uploaded:", glbUrl);

      arModelUrl.value = glbUrl;

      const designId = projectStore.designId;
      if (designId) {
        arPageUrl.value = `${window.location.origin}/?id=${encodeURIComponent(designId)}&ar=true`;
      }

      if (supportsAR) {
        setTimeout(() => tryLaunchAR(0), 1000);
      }
    } catch (error) {
      console.error("[AR Auto-Launch] Failed:", error);
      arError.value = "Failed to prepare 3D model. Please try again.";
      arAutoLaunch.value = false;
    } finally {
      isExportingAR.value = false;
    }
  };

  const closeARModal = () => {
    if (arRetryTimer.value) {
      clearTimeout(arRetryTimer.value);
      arRetryTimer.value = null;
    }
    arModalOpen.value = false;
    arModelUrl.value = null;
    arPageUrl.value = null;
    arError.value = null;
    arAutoLaunch.value = false;
  };

  const handleARRetry = () => {
    handleARAutoLaunch();
  };

  const cleanupAR = () => {
    if (arRetryTimer.value) {
      clearTimeout(arRetryTimer.value);
      arRetryTimer.value = null;
    }
  };

  return {
    arModalOpen,
    arModelUrl,
    arPageUrl,
    arError,
    isExportingAR,
    arAutoLaunch,
    handleARAutoLaunch,
    closeARModal,
    handleARRetry,
    cleanupAR,
  };
}
